import { ComponentSelectionStorageService } from 'src/tmc-common/services/component-selection-storage.service';
import { Component, OnInit, OnDestroy, ViewEncapsulation, ViewChild } from '@angular/core';
import { AbstractWidgetComponent, WidgetStyle } from '@sdbn/appclient';
import { TranslationService } from '@sdbn/common';
import { ContextChange } from '@sdbn/appclient';
import { VisitorUpfrontRegistationFilterModel, VisitReservation } from '../visitor-upfront-registration-list.model';
import { interval, Subscription } from 'rxjs';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort, SortDirection } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { VisitorUpfrontRegistrationService } from '../visitor-upfront-registration-list.service';
import { TMCClientParameters } from 'src/tmc-common/tmc-client-parameters';
import { ConfigurationService } from '@sdbn/appclient';
import { environment } from 'src/environments/environment';

const TABLE_SELECTION_KEY = 'visitor-upfront-registration-list.selection';


@Component({
  selector: 'app-visitor-upfront-registration-list',
  templateUrl: './visitor-upfront-registration-list.component.html',
  styleUrls: ['./visitor-upfront-registration-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VisitorUpfrontRegistrationListComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  // controls if an initial load of transport must be performed. Must be executed only once!
  initialLoad = true;

  filterModel = new VisitorUpfrontRegistationFilterModel();
  filterSubscription: Subscription;

  // Table parameters
  dataSource = new MatTableDataSource(new Array<VisitReservation>());
  dataSourceSubscription: Subscription;
  selection = new SelectionModel<VisitReservation>();
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  defaultVisibleColumns = ['hint', 'name', 'firstname', 'company', 'visitedPerson', 'visitedPersonName', 'visitedPersonDepartment', 'numberPlate', 'plannedArrival',
    'arrival', 'leave', 'registrationCode', 'role', 'instructionsStatus', 'supplementaryEquipments', 'invitationSentDate', 'lastTerminalRegistrationDate', 'lastClientRegistrationDate'];
    
  columnsToDisplay: string[] = [];

  sortEmptyValuestoEndForColumns: string[] = ['arrival'];
  sortColumn = 'plannedArrival';
  sortDirection: SortDirection = 'asc' as SortDirection;
  // END Table parameters

  refreshEnabled = true;
  refreshInterval = 30000;
  refreshTimer: Subscription;

  constructor(
    public translationService: TranslationService,
    private visitorUpfrontRegistrationService: VisitorUpfrontRegistrationService,
    private componentSelectionStorageService: ComponentSelectionStorageService,
    private cps: ConfigurationService) {

    super();

    this.setupColumns();

    // the sorting based on ViewChild, needs to be initialized after view init because of *ngIf
    this.dataSource.sortingDataAccessor = (item: VisitReservation, property) => {
      return item[property];
    };
    this.dataSource.data = [];
    this.dataSource.sortData = this.sortData.bind(this);
    // we need to subscribe to data source to react on changes in visible rows
    this.dataSourceSubscription = this.dataSource.connect().subscribe(
      () => this.onVisibleRowsChanged()
    );

    this.registerRefreshTimer();
  }

  ngOnInit() {

    // must be set in ngOnInit!
    this.dataSource.sort = this.sort;
    this.dataSource.sort.disableClear = true;

    /** The rule how the entered filter string is matched against rows' data values. */
    this.dataSource.filterPredicate = (data: VisitReservation, filterString: string) => {

      if (!this.checkFulltextFilter(data, this.filterModel.filterText)) {
        return false;
      }

      if (!this.checkAdditionalFilters(data)) {
        return false;
      }

      return true;
    };

  }

  ngOnDestroy() {

    super.ngOnDestroy();

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }
  }

  private setupColumns() {
    const columnProperty = this.cps.getClientParamValue(TMCClientParameters.P_VISITOR_REGISTRATION_OVERVIEW_COLUMNS, null);
    if (columnProperty != null && String(columnProperty).length > 0) {

      const columProperties = String(columnProperty).split('|');
      for (const p of columProperties) {
        const checkColumn = this.defaultVisibleColumns.indexOf(p) !== -1;
        if (checkColumn) {
          this.columnsToDisplay.push(p);
        }
      }
    } else {
      this.columnsToDisplay.push(...this.defaultVisibleColumns);
    }
    this.columnsToDisplay.push('filler');
    console.log('columns are', this.columnsToDisplay);
  }

  private sortData(data: VisitReservation[], sort: MatSort) {
    if (!sort.active || sort.direction === '') {
      return data;
    }
    const isAsc = this.sort.direction === 'asc';

    const emptyToEnd = this.sortEmptyValuestoEndForColumns != null
      && this.sortEmptyValuestoEndForColumns.indexOf(sort.active) !== -1;

    return data.sort((a: VisitReservation, b: VisitReservation) => {

      // dedicated sort value first
      let value1 = a[sort.active + 'SortValue'];
      let value2 = b[sort.active + 'SortValue'];

      // default if no sort value is available
      if (value1 == null || value2 == null) {
        value1 = a[sort.active];
        value2 = b[sort.active];
      }

      if (emptyToEnd) {
        // Treat null or empty values always as greater
        if ((value1 == null || value1 === '') && (value2 == null || value2 === '')) {
          return 0;
        }
        if (value1 == null || value1 === '') {
          return +1;
        }
        if (value2 == null || value2 === '') {
          return -1;
        }
      }
      return this.compare(value1, value2, isAsc);
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    let t = 0;
    if ((a == null || a === '') && (b == null || b === '')) {
      t = 0;
    } else if (a == null || a === '') {
      t = -1;
    } else if (b == null || b === '') {
      t = 1;
    } else {
      t = (a < b ? -1 : 1);
    }
    return t * (isAsc ? 1 : -1);
  }

  private checkFulltextFilter(data: VisitReservation, filterString: string): boolean {
    const normalizedFilterString = filterString == null ? '' : filterString.toLowerCase();

    for (const k of Object.keys(data)) {
      const fieldValue = data[k];
      if (fieldValue != null) {
        const normalizedFieldValue = String(fieldValue).toLowerCase();
        if (normalizedFieldValue.indexOf(normalizedFilterString) > -1) {
          return true;
        }
      }
    }
    return false;
  }

  private checkAdditionalFilters(data: VisitReservation): boolean {

    if (this.filterModel.visitorTypes == null || this.filterModel.visitorTypes.length === 0) {
      return true;
    }

    for (const scope of this.filterModel.visitorTypes) {
      if (data.visitorType === scope) {
        return true;
      }
    }
    return false;
  }

  private registerRefreshTimer() {

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (environment.autoreload && this.refreshEnabled) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(() => this.loadVisits());
    } else {
      console.log('Autoreload visits disabled');
    }
  }

  public getHintClasses(visit: VisitReservation): string[] {
    const hints: string[] = [];
    if (visit.hintStyle) {
      for (const s of visit.hintStyle.split(' ')) {
        hints.push(s);
      }
    }
    return hints;
  }

  public getRowStyles(visit: VisitReservation) {
    const styles: string[] = [];
    if (this.selection.isSelected(visit)) {
      styles.push('selected');
    } else {
      styles.push('deselected');
    }
    if (visit.rowStyle != null) {
      styles.push(visit.rowStyle);
    }
    return styles;
  }

  public toggleSelection(reservation: VisitReservation) {
    this.selection.select(reservation);
    this.dashboardContext.setContextValue('selected-visitor-upfront-registration', this.selection.isEmpty() ? null : reservation);
    this.storeTableSelection();
  }

  private storeTableSelection() {
    this.componentSelectionStorageService.saveToSession(TABLE_SELECTION_KEY, this.selection.selected);
  }

  private onVisibleRowsChanged() {
    const autoselectEnabled = this.cps.isEnabled(TMCClientParameters.P_VISITS_PERSONAL_VISIT_AUTO_SELECT_SINGLE_SEARCH_RESULT, false);
    if (autoselectEnabled && this.dataSource.filteredData.length === 1) {
      this.selection.select(this.dataSource.filteredData[0]);
    }
  }

  public loadVisits() {

    this.visitorUpfrontRegistrationService.getAllVisitReservations().subscribe(
      response => {
        this.dataSource.data = response;
        this.restoreTableSelection();
        this.refreshDataSourceFilter();
        this.createTranslatedValuesOnList();
      },
      error => {
        this.dataSource.data = [];
        console.log('Error wihle Loading list of vists!', error);
      });
  }

  private restoreTableSelection() {
    this.selection.clear();
    const storedSelection: VisitReservation[] = this.componentSelectionStorageService.getFromSession(TABLE_SELECTION_KEY);

    const restoredSelection: VisitReservation[] = [];
    if (storedSelection != null && storedSelection.length > 0) {
      for (const s of storedSelection) {
        for (const row of this.dataSource.data) {
          if (s.id === row.id) {
            restoredSelection.push(row);
          }
        }
      }
    } else if (this.dataSource.data != null && this.dataSource.data.length > 0) {
      // Fallback: use first available row
      restoredSelection.push(this.dataSource.data[0]);
    }
    this.selection.select(...restoredSelection);
    this.dashboardContext.setContextValue('selected-visitor-upfront-registration', this.selection.isEmpty() ? null : restoredSelection[0]);
    this.storeTableSelection();
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
    this.initializeComponent();
  }

  private initializeComponent() {
    if (this.widgetReference != null && this.dashboardContext != null) {

      this.refreshEnabled = this.widgetReference.refreshEnabled == null ? true : this.widgetReference.refreshEnabled;
      if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
        this.refreshInterval = this.widgetReference.refreshInterval * 1000;
      } else {
        this.refreshInterval = 30 * 1000;
      }
      this.registerRefreshTimer();

      this.loadVisits();

      this.dashboardContext.contextChange$.subscribe(contextChange => {
        if (contextChange.key === 'visitor-upfront-registration-selection') {
          this.refreshDataSourceFilter();
        }
        if (contextChange.key === 'visitor-upfront-registration-changed') {
          this.loadVisits();
        }
      });

    }
  }

  private refreshDataSourceFilter() {
    const model = this.dashboardContext?.context.get('visitor-upfront-registration-selection');
    this.filterModel = model;
    console.log(JSON.stringify(model));
    this.dataSource.filter = JSON.stringify(model);
  }

  createTranslatedValuesOnList() {
    for (const visitReservation of this.dataSource.data) {
      visitReservation.role = this.translationService.translate(visitReservation.roleDictionaryKey);
    }
  }
  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
